import * as React from 'react';
import * as cx from 'classnames';
import S, { DataSignal } from 's-js';
import SuperCritical, { SuperCriticalComponent, SuperCriticalReference } from 'lib/supercritical';
import { noDefault, noPropagation } from 'lib/eventWrappers';

import { PolygonCoords } from 'data/models/ZoneCoordinates';

import { BerthProfile, AmenityViewModel, CapabilityAssignmentViewModel, ZoneOperationsMode } from './model';

import { NumericInput } from 'portcall/numericinput/view';
import { PCDismissible } from 'portcall/pcdismissible/view';
import { PCModal } from 'portcall/pcmodal/view';
import { PierGroupFootmarksView } from 'portcall/piergroupfootmarks/view';
import { PostalAddressView } from 'portcall/postaladdress/view';
import { ToggleButton } from 'portcall/togglebutton/view';
import { ToggleableMeasurementView } from 'portcall/toggleablemeasurement/view';
import { ValidationErrorView } from 'core/validationerror/view';

import { MapView } from './mapview';

export const BerthProfileView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div className="berthprofile pc-page-fixedwidth">
        <HeaderView model={m} />
        <PageLeftSide model={m} />
        <PageRightSide model={m} />
        <NotesEditor model={m} />
        <EditFootmarksModal model={m} />
        <ZoneExistsModal model={m} />
    </div>
);

const HeaderView = SuperCritical(({ model: m } : { model : BerthProfile }) => {
    const location = m.Berth()?.Location();
    return (
        <div className="header-shell">
            <div className="pc-actions-links-header">
                <a className="pc-link-small pc-back-link" href="" onClick={noDefault(m.GoBack)}>&lt;&lt; Back</a></div>
            {location &&
                <div>
                    {!location.EditingName() ?
                        <label className="name pc-page-title" onClick={m.Model.Ref.CanEdit() ? location.ToggleEditingName : undefined}>
                            {location.Model.Name()}
                        </label>
                    :
                        <input
                            className="name-edit"
                            type="text"
                            value={location.Model.Name()}
                            onChange={e => location.Model.Name(e.currentTarget.value)}
                            onBlur={location.ToggleEditingName}
                            autoFocus
                        />
                    }
                </div>
            }
        </div>
    );
});

const PageLeftSide = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div className="leftside">
        <MapView model={m} />
        <LocationParticularsView model={m} />
        <BerthAmenitiesView model={m} />
        <BerthCapabilitiesView model={m} />
    </div>
);

const LocationParticularsView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div className=" particulars pc-card profile-body clearfix">
        <div className="pc-card-header pc-card-title">LOCATION PARTICULARS</div>
        <PortSelector model={m} />
        <div className="location">
            <table className="interior">
                <tbody>
                    <tr>
                        <td className="left">
                            <label>Physical Address</label>
                        </td>
                        <BerthAddressView model={m} />
                    </tr>
                    <tr><td>&nbsp;</td></tr>
                    <tr>
                        <td className="left">
                            <label>Latitiude &amp; Longitude</label>
                        </td>
                        <ZoneCoordinatesView model={m} />
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
);

const PortSelector = SuperCritical(({ model: m } : { model : BerthProfile }) => {
    const pierGroup = m.Berth()?.PierGroup();
    return (
        <div className="port">
            <label className="label-port">Port</label>
            {pierGroup?.Model.Port() &&
                <label className="hashtag" hidden={pierGroup.Editing.Port()}
                    onClick={m.Model.Ref.CanEdit() ? pierGroup.Editing.TogglePort : undefined}>
                    {pierGroup.Model.Port()!.Name()}
                </label>
            }
            {pierGroup?.Editing.Port() &&
                <div className="port-selector">
                    <select
                        className="pc-select"
                        value={pierGroup.Model.PortId()}
                        onChange={e => pierGroup.Model.PortId(+e.currentTarget.value)}
                        onBlur={pierGroup.Editing.TogglePort}
                        autoFocus
                    >
                        {m.Model.Ref.Ports().map(port =>
                            <option key={port.Id()} value={port.Id()}>{port.Name()}</option>
                        )}
                    </select>
                    <span className="pc-select-chevron"></span>
                </div>
            }
        </div>
    );
});

const BerthAddressView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <td className={cx({
            'pc-show-editcontrols': !!m.Berth()?.Editing.Address(),
            'pc-show-viewcontrols': !m.Berth()?.Editing.Address()
        })}>
        {m.Model.Ref.CanEdit() && m.Berth() &&
            <a href="" className="address-edit-link" onClick={noDefault(m.Berth()!.Editing.ToggleAddress)}>
                {m.Berth()!.Editing.Address() ? 'Done' : 'Edit'}
            </a>
        }
        {m.Berth()?.PostalAddress() && <PostalAddressView model={m.Berth()!.PostalAddress()!} />}
    </td>
);

const ZoneCoordinatesView = SuperCritical(({ model: m } : { model : BerthProfile }) => {
    const location = m.Berth()?.Location(),
        coords = location?.Coords();
    return (
        <td>
            <div className="coords-shell">
                {
                    coords ?
                        coords instanceof PolygonCoords ?
                            coords.coords.map(latLong =>
                                <div className="particulars-desc" key={latLong.toString()}>{latLong.toString()}</div>
                            ) :
                            <div>
                                <div className="particulars-desc">{coords.latLong.toString()}</div>
                                <div className="particulars-desc">Radius {coords.radius}m</div>{/* TH: Hmmm... So radius is always in meters?*/}
                            </div> :
                        <div className="particulars-desc">No zone coordinates selected</div>
                }
            </div>
        </td>
    );
});

const BerthAmenitiesView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div className="amenities pc-card profile-body clearfix">
        <div className="pc-card-header pc-card-title">
            AMENITIES
            {' '}
            {m.Model.Ref.CanEdit()
                && m.Berth()
                && m.Berth()!.Amenities.AvailableAmenityTypes().length > 0 &&
                <a className="add-link" href="" onClick={noDefault(m.Berth()!.Amenities.Add)}>+ Add New</a>
            }
        </div>
        <ul className="amenity-list">
            {m.Berth()?.Amenities.Amenities().map(amenity =>
                <BerthAmenityView amenity={amenity} model={m} key={amenity.Model.Id()} />
            )}
        </ul>
    </div>
);

const BerthAmenityView = SuperCritical(({ amenity: a, model: m } : { amenity : AmenityViewModel, model : BerthProfile }) => {
    const amenityType = a.Model.Amenity();
    return (
        <li>
            {!a.Editing() ?
                <span className="list-span" onClick={m.Model.Ref.CanEdit() ? a.ToggleEditing : undefined}>
                    <label className="amenity-type">{amenityType?.Name()}</label>
                    <label className="amenity-desc">{amenityType?.Description()}</label>
                    {m.Model.Ref.CanEdit() &&
                        <a className="amenity-delete pc-close-icon" onClick={noPropagation(a.Delete)}></a>
                    }
                </span>
            :
                <span className="list-span">
                    <select className="pc-select amenity-type-edit"
                        value={a.Model.AmenityId()}
                        onChange={e => a.Model.AmenityId(+e.currentTarget.value)}>
                        {m.Berth()?.Amenities.AvailableForSlot(a).map(_amenityType =>
                            <option value={_amenityType.Id()} key={_amenityType.Id()}>{_amenityType.Name()}</option>
                        )}
                    </select>
                    <span className="pc-select-chevron"></span>
                    <a href="" className="amenity-delete" onClick={noDefault(a.ToggleEditing)}>Done</a>
                </span>
            }
        </li>
    );
});

const BerthCapabilitiesView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div className="capabilities pc-card profile-body clearfix">
        <div className="pc-card-header pc-card-title">
            CAPABILITIES
            {' '}
            {m.Model.Ref.CanEdit()
                && m.Berth()
                && m.Berth()!.Capabilities.AvailableCapabilities().length > 0 &&
                <a className="add-link" href="" onClick={noDefault(m.Berth()!.Capabilities.Add)}>+Add New</a>
            }
        </div>
        <ul className="capability-list">
            {m.Berth()?.Capabilities.Capabilities().map(cap =>
                <BerthCapabilityView capAssignment={cap} model={m} key={cap.Model.Id()} />
            )}
        </ul>
    </div>
);

const BerthCapabilityView = SuperCritical(({ capAssignment: c, model: m } : { capAssignment : CapabilityAssignmentViewModel, model : BerthProfile }) => {
    const cap = c.Model.Capability();
    return (
        <li>
            {!c.Editing() ?
                <span className="list-span" onClick={m.Model.Ref.CanEdit() ? c.ToggleEditing : undefined}>
                    <label className="capability-type">{cap?.Name()}</label>
                    {m.Model.Ref.CanEdit() &&
                        <a className="capability-delete pc-close-icon" onClick={noPropagation(c.Delete)}></a>
                    }
                </span>
            :
                <span className="list-span">
                    <select className="pc-select capability-type-edit"
                        value={c.Model.CapabilityId()}
                        onChange={e => c.Model.CapabilityId(+e.currentTarget.value)}>
                        {m.Berth()?.Capabilities.AvailableForSlot(c).map(_cap =>
                            <option value={_cap.Id()} key={_cap.Id()}>{_cap.Name()}</option>
                        )}
                    </select>
                    <span className="pc-select-chevron"></span>
                    <a href="" className="capability-delete" onClick={noDefault(c.ToggleEditing)}>Done</a>
                </span>
            }
        </li>
    );
});

const PageRightSide = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div className="rightside">
        <div className="settings pc-card profile-body clearfix">
            <div className="title selected">
                <label className="selected">SETTINGS</label>
            </div>
            {/* TODO -- Re-organize for piergroup settings vs berth settings */}
            <SchedulingAuthorityView model={m} />
            <BerthMeasurementsView model={m} />
            <TimeBufferView model={m} />
            {/* <AutoCalculateBillsView model={m} />
            <AvailableMooringHoursView model={m} />
            <MultiVesselBerthingView model={m} /> */}
            <BerthTransitOffsetView model={m} />
            <IsPortExternalView model={m} />
            <AgentChangeWindowView model={m} />
            <ConfigureChangeWindowModal model={m} />
            <PreventAgentOverlapsView model={m} />
            <RequireKnownVesselsView model={m} />
        </div>
    </div>
);

const SchedulingAuthorityView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div className="setting-top">
        <label className="sched-auth setting-label pc-text-label">Scheduling Authority</label>
        {m.Berth() &&
            <label className="authority pc-text-label" hidden={m.Berth()!.Editing.SchedulingAuthority()} onClick={m.Model.Ref.CanEdit() ? m.Berth()!.Editing.ToggleSchedulingAuthority : undefined}>
                {m.Berth()!.Model.SchedulingAuthority() ? m.Berth()!.Model.SchedulingAuthority()!.Name() : 'None'}
            </label>
        }
        {m.Berth()?.Editing.SchedulingAuthority() &&
            <div className="select-shell">
                <select
                    className="pc-select" value={m.Berth()!.Model.SchedulingAuthorityId() === null ? '' : '' + m.Berth()!.Model.SchedulingAuthorityId()}
                    onChange={e => m.Berth()!.Model.SchedulingAuthorityId(e.currentTarget.value === '' ? null : +e.currentTarget.value)}
                    onBlur={m.Berth()!.Editing.ToggleSchedulingAuthority}
                    autoFocus
                >
                        <option value="" key="">None</option>
                        {m.Organizations().map(schedAuth =>
                            <option value={schedAuth.Id()} key={schedAuth.Id()}>{schedAuth.Name()}</option>
                        )}
                </select>
                <span className="pc-select-chevron"></span>
            </div>
        }
    </div>
);

const BerthMeasurementsView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div className="dimension">
        <UOMToggle model={m} />
        <TotalLengthView model={m} />
        <FootmarksView model={m} />
        <MaxDraftAllowedView model={m} />
        <SideToSelectorView model={m} />
        <CompassPointsView model={m} />
        <LengthBufferView model={m} />
        <StartOverhangView model={m} />
        <EndOverhangView model={m} />
    </div>
);

const UOMToggle = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    m.Berth() &&
        <div className="uom-selector">
            <label className="text">lengths in&nbsp;</label>
            <ToggleButton className="text" onLabel="feet" offLabel="meters"
                checked={m.Berth()!.Measurements.MeasurementsInFeet} />
        </div>
);

const TotalLengthView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div className="maximum total-length">
        <div>
            <label className="dim-label">Total Length</label>
            {m.Berth() && <ToggleableMeasurementView model={m.Berth()!.Measurements.ToggleableLength} textonly />}
        </div>
        <div className="setting-desc">
            The maximum linear distance that is available for scheduling.
        </div>
    </div>
);

const FootmarksView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div className="footmark">
        <div>
            <label className="footmark-header">Footmarks</label>
            {m.Model.Ref.CanEditPort() && m.Berth()?.PierGroup() &&
                <a
                    href=""
                    className="edit-footmarks-link"
                    onClick={noDefault(() => m.Berth()!.PierGroup()!.ShowEditFootmarks(true))}
                >
                    Edit
                </a>
            }
        </div>
        <div className="setting">
            <label className="footmark-label">Begin</label>
            {m.Berth() && <ToggleableMeasurementView model={m.Berth()!.Measurements.ToggleableStartMark} textonly />}
        </div>
        <div className="setting">
            <label className="footmark-label">End</label>
            {m.Berth() && <ToggleableMeasurementView model={m.Berth()!.Measurements.ToggleableEndMark} textonly />}
        </div>
    </div>
);

const EditFootmarksModal = SuperCritical(({ model: m } : { model : BerthProfile }) => {
    const popup = m.Berth()?.PierGroup()?.EditFootmarks();
    return (
        <PCModal className="edit-footmarks-modal" shown={() => !!popup} onBackDropClick={popup?.Cancel}>
            <div className="modal-content pc-card">
                <div className="pc-card-header pc-card-title">{m.Berth()?.Location()?.Model.Name()}'s Group</div>
                {popup &&
                    <div className="modal-body">
                        <PierGroupFootmarksView model={popup.Footmarks} />
                        <div className="buttons">
                            <a href="" className="cancel-btn" onClick={noDefault(popup.Cancel)}>Cancel</a>
                            <button
                                className="submit-btn pc-button pc-button-primary"
                                onClick={noDefault(popup.Save)}
                                disabled={!popup.CanSave()}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                }
            </div>
        </PCModal>
    );
});

const MaxDraftAllowedView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div className="maximum">
        <div>
            <label className="dim-label">Max Vessel Draft</label>
            {m.Berth() && <ToggleableMeasurementView model={m.Berth()!.Measurements.ToggleableMaxDraft} disabled={!m.Model.Ref.CanEdit()} />}
        </div>
        <div className="setting-desc">
            Indicate the safest vessel draft to prevent scheduling vessels with deeper drafts.
        </div>
    </div>
);

const SideToSelectorView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div>
        <div className="maximum side-to-descr">
            <div>
                <label className="dim-label">Side-To Settings</label>
            </div>
            <div className="setting-desc">
                When the ship is <b className="port side-to">port side-to</b> at this berth, is the bow at the lower footmark or the higher footmark?
            </div>
        </div>
        <div className="side-to-selectors">
            <div className="side-to-selector-option">
                <div className={cx('side-to-selector-btn', {
                        'side-to-selector-btn-selected': !!m.Berth()?.Model.PortSideVesselHasBowToLowFootmark()
                    })}
                    onClick={m.Model.Ref.CanEdit() ? () => m.Berth()?.Model.PortSideVesselHasBowToLowFootmark(true) : undefined}>
                </div>
                <span className="side-to-selector-label">Low Footmark</span>
            </div>
            <div className="side-to-selector-option">
                <div className={cx('side-to-selector-btn', {
                        'side-to-selector-btn-selected': !!(m.Berth() && !m.Berth()!.Model.PortSideVesselHasBowToLowFootmark())
                    })}
                    onClick={m.Model.Ref.CanEdit() ? () => m.Berth()?.Model.PortSideVesselHasBowToLowFootmark(false) : undefined}>
                </div>
                <span className="side-to-selector-label">High Footmark</span>
            </div>
        </div>
    </div>
);

const CompassPointsView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div>
        <div className="maximum pier-orientation-descr">
            <div>
                <label className="dim-label">Pier Orientation</label>
            </div>
            <div className="setting-desc">
                Choose the cardinal point for the pier direction.
                If the footmarks increase from N to S, choose N for the low footmark.
            </div>
        </div>
        <div className="pier-orientation-selectors">
            <div>
                <span className="pier-orientation-selector-label">Low</span>
                {' '}
                <span className="pier-orientation-selector-view">{m.Berth() && m.Berth()!.CompassPoints.LowFootmarkCompassAbbreviation()}</span>
                {' '}
                <div className="pier-orientation-edit-container">
                    {m.Model.Ref.CanEdit() && m.Berth() &&
                        <span className="pier-orientation-selector-edit fa fa-chevron-down"
                            onClick={m.Berth()!.CompassPoints.ToggleCompassPointsMenu}></span>
                    }
                    {m.Berth()?.CompassPoints.ShowCompassPointsMenu() &&
                        <PCDismissible className="pier-orientation-compass-points-menu" onDismiss={m.Berth()!.CompassPoints.ToggleCompassPointsMenu}>
                            {m.Berth()!.CompassPoints.AvailablePoints.map(compass =>
                                <div className={cx('pier-orientation-compass-points-menu-item', {
                                        selected: m.Berth()!.Model.LowFootmarkCompassPoint() === compass.Id
                                    })}
                                    onClick={() => m.Berth()!.CompassPoints.SelectCompassPoint(compass.Id)}
                                    key={compass.Id}>
                                    {compass.Abbreviation}
                                </div>
                            )}
                        </PCDismissible>
                    }
                </div>
            </div>
            <div className="pier-orientation-high-row">
                <span className="pier-orientation-selector-label">High</span>
                {' '}
                <span className="pier-orientation-selector-view">{m.Berth()?.CompassPoints.HighFootmarkCompassAbbreviation()}</span>
            </div>
        </div>
    </div>
);

const StartOverhangView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div className="maximum">
        <label className="dim-label">First Overhang</label>
        {m.Berth()?.PierGroup() && <ToggleableMeasurementView model={m.Berth()!.PierGroup()!.Measurements.ToggleableStartOverhang} disabled={!m.Model.Ref.CanEdit()} />}
        <div className="setting-desc">
            Designate the distance a vessel can extend outside the berth's first footmark.
        </div>
    </div>
);

const EndOverhangView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div className="maximum">
        <label className="dim-label">Final Overhang</label>
        {m.Berth()?.PierGroup() && <ToggleableMeasurementView model={m.Berth()!.PierGroup()!.Measurements.ToggleableEndOverhang} disabled={!m.Model.Ref.CanEdit()} />}
        <div className="setting-desc">
            Designate the distance a vessel can extend outside the berth's final footmark.
        </div>
    </div>
);

const LengthBufferView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div className="maximum">
        <div>
            <label className="dim-label">Length Buffer</label>
            {m.Berth() && <ToggleableMeasurementView model={m.Berth()!.Measurements.ToggleableLengthBuffer} disabled={!m.Model.Ref.CanEdit()} />}
        </div>
        <div className="setting-desc">
            Assign extra space to a vessel's bow and stern to maintain space between vessels.
        </div>
    </div>
);

const TimeBufferView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div>
        <div>
            <label className="setting-label">Time Buffer</label>
            {m.Berth() && <NumericInput value={m.Berth()!.Model.TimeBuffer} disabled={!m.Model.Ref.CanEdit()} />}
        </div>
        <div className="setting-desc">
            Specify the number of minutes required between reservations.
        </div>
    </div>
);

const AutoCalculateBillsView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div>
        <div>
            <label className="setting-label">Auto-calculate Bills</label>

            {/*Disabling Auto-calculate bills until billing module is available. To enable, delete the following ToggleButton and uncomment the one below it.*/}
            <ToggleButton
                className="toggler"
                onLabel="Yes"
                offLabel="No"
                checked={() => false}
                disabled
            />
            {/*m.Berth() &&
                <ToggleButton
                    className="toggler"
                    onLabel="Yes"
                    offLabel="No"
                    checked={m.Berth()!.Model.AutoCalculateBills}
                    disabled={!m.Model.Ref.CanEdit()}
                />
            */}
        </div>
        <div className="setting-desc">
            Select Yes to automatically calculate dockage and wharfage from the schedule. Select No when automatic billing doesn&#39;t apply, e.g. leased piers.
        </div>
    </div>
);

const AvailableMooringHoursView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div>
        <div>
            <label className="setting-label">Available Mooring Hours</label>
            {m.Berth() &&
                <ToggleButton
                    className="toggler"
                    onLabel="Daylight"
                    offLabel="24/7"
                    checked={m.Berth()!.Model.DaylightMooringOnly}
                    disabled={!m.Model.Ref.CanEdit()}
                />
            }
        </div>
        <div className="setting-desc">
            Note whether mooring is available 24/7 or only during daytime hours.
        </div>
    </div>
);

const MultiVesselBerthingView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div>
        <div>
            <label className="setting-label">Multi-Vessel Berthing</label>
            {m.Berth() &&
                <ToggleButton
                    className="toggler"
                    onLabel="Multi"
                    offLabel="Single"
                    checked={m.Berth()!.Model.MultiVesselBerthing}
                    disabled={!m.Model.Ref.CanEdit()}
                />
            }
        </div>
        <div className="setting-desc">
            Select Multi to allow multiple vessels to be scheduled across the pier. Choose Single to limit only one vessel to be scheduled at the pier..
        </div>
    </div>
);

const BerthTransitOffsetView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div>
        <div>
            <label className="setting-label">Berth Transit Offset</label>
            {m.Berth() && <NumericInput value={m.Berth()!.Model.BerthTransitOffset} disabled={!m.Model.Ref.CanEdit()} />}
        </div>
        <div className="setting-desc">
            Specify the average number of minutes from a common harbor entrance landmark to the berth. This offset is used to calculate the default inbound time.
        </div>
    </div>
);

const IsPortExternalView = SuperCritical(({ model: m } : { model : BerthProfile }) => {
    const l = m.Berth()?.Location();
    return (
        <div>
            <div>
                <label className="setting-label">Is Port External</label>
                {l &&
                    <ToggleButton
                        className="toggler"
                        onLabel="Yes"
                        offLabel="No"
                        checked={l.Model.IsPortExternal}
                        disabled={!m.Model.Ref.CanEdit()}
                    />
                }
            </div>
            <div className="setting-desc">
                Select Yes to indicate that this location is outside the port. E.g. anchorages.
            </div>
        </div>
    )
});

const AgentChangeWindowView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div>
        <div className="change-window-setting">
            <label className="setting-label">Reservation Window</label>
            {m.Berth()?.PierGroup() &&
                <span>
                    <ToggleButton
                        className="toggler"
                        onLabel="Yes"
                        offLabel="No"
                        checked={m.Berth()!.PierGroup()!.ChangeWindowToggleChecked}
                        disabled={!m.Model.Ref.CanEdit()}
                    />
                    {m.Berth()!.PierGroup()!.Model.AgentChangeWindowHours() !== null &&
                        <ChangeWindowInput
                            value={m.Berth()!.PierGroup()!.NonNullChangeWindow}
                            disabled={!m.Model.Ref.CanEdit()}
                        />
                    }
                </span>
            }
        </div>
        <ValidationErrorView error={() => m.Berth()?.PierGroup()?.Validation()['AgentChangeWindowHours']} />
        <div className="setting-desc">
            Select Yes to set a window that reservations can be edited from their initial requests.
            Select No to avoid a reservation window restriction.
        </div>
    </div>
);

const ConfigureChangeWindowModal = SuperCritical(({ model: m } : { model : BerthProfile }) => {
    const popup = m.Berth()?.PierGroup()?.ConfigureChangeWindowPopup();
    return (
        <PCModal className="configure-change-window-modal" shown={() => !!popup} onBackDropClick={popup?.Cancel}>
            <div className="modal-content pc-card">
                <div className="pc-card-header pc-card-title">Configure Reservation Window</div>
                {popup &&
                    <form className="modal-body" onSubmit={noDefault(popup.Commit)}>
                        <div>
                            <ChangeWindowInput value={popup.ChangeWindowHours} autoFocus />
                        </div>
                        <div className="buttons">
                            <a href="" className="cancel-btn" onClick={noDefault(popup.Cancel)}>Cancel</a>
                            <button className="submit-btn pc-button pc-button-primary">Save</button>
                        </div>
                    </form>
                }
            </div>
        </PCModal>
    );
});

interface ChangeWindowInputProps {
    value : DataSignal<number>;
    autoFocus? : boolean;
    disabled? : boolean;
}

class ChangeWindowInput extends SuperCriticalComponent<ChangeWindowInputProps> {
    // Long enough for the animation on modal opening to complete
    static readonly FOCUS_DELAY_MS = 200;

    inputRef : (el : HTMLInputElement | null) => void;

    constructor(props : ChangeWindowInputProps) {
        super(props);

        // Focus the input if requested. Do this w/ a timeout instead of a simple autoFocus property
        // to wait for the modal animation to complete in the case of the .configure-change-window-modal.
        this.inputRef = SuperCriticalReference(this, ({ autoFocus }, el) => {
            if (autoFocus) {
                const timeoutId = setTimeout(() => {
                    el.focus();
                    el.select();
                }, ChangeWindowInput.FOCUS_DELAY_MS);
                S.cleanup(() => clearTimeout(timeoutId));
            }
        });
    }

    srender({ value, disabled } : ChangeWindowInputProps) {
        return (
            <span className="change-window-input-container">
                <NumericInput
                    inputRef={this.inputRef}
                    className="change-window-input"
                    value={value}
                    disabled={disabled}
                />
                <span className="change-window-uom">hours</span>
            </span>
        );
    }
}

const PreventAgentOverlapsView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div>
        <div>
            <label className="setting-label">Prevent Overlaps</label>
            {m.Berth()?.PierGroup() &&
                <ToggleButton
                    className="toggler"
                    onLabel="Yes"
                    offLabel="No"
                    checked={m.Berth()!.PierGroup()!.Model.PreventAgentOverlaps}
                    disabled={!m.Model.Ref.CanEdit()}
                />
            }
        </div>
        <div className="setting-desc">
            Select Yes to prevent reservations from being requested on top of other reservations.
            Select No to allow users to schedule reservations on top of others.
        </div>
    </div>
);

const RequireKnownVesselsView = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div>
        <div>
            <label className="setting-label">Require Known Vessels</label>
            {m.Berth()?.PierGroup() &&
                <ToggleButton
                    className="toggler"
                    onLabel="Yes"
                    offLabel="No"
                    checked={m.Berth()!.PierGroup()!.Model.RequireKnownVessels}
                    disabled={!m.Model.Ref.CanEdit()}
                />
            }
        </div>
        <div className="setting-desc">
            Select Yes to prevent reservations with length-only vessel submissions.
            Select No to allow length-only reservations.
        </div>
    </div>
);

// TH 10/19/2016: Reverting Notes from a Quill HTML editor to a plain textarea b/c of XSS concerns.
// It looks like the latest version of Quill no longer exports HTML but a custom JSON format.
// http://quilljs.com/docs/api/
// So if we'd like to bring a rich text editor back in at some point, that might be a good way to go.
const NotesEditor = SuperCritical(({ model: m } : { model : BerthProfile }) =>
    <div className="notes pc-card profile-body clearfix">
        <div className="pc-card-header pc-card-title">NOTES</div>
        {m.Berth() && <textarea className="editor"
            disabled={!m.Model.Ref.CanEdit()}
            value={m.Berth()!.Model.Notes() || ''}
            onChange={e => m.Berth()!.Model.Notes(e.currentTarget.value)} />
        }
    </div>
);

const ZoneExistsModal = SuperCritical(({ model: m } : { model : BerthProfile }) => {
    const z = m.Berth()?.Location()?.ZoneOperationsModal();
    return (
        <PCModal className="zone-operations-modal" shown={() => !!z}>
            <div className="modal-content">
                <div className="modal-body clearfix">
                    {z && (z.Mode() === ZoneOperationsMode.PromptOverwrite ?
                        <div>
                            Overwrite existing zone?
                            <br />
                            <a href="" className="overwrite-choice" onClick={noDefault(z.ConfirmOverwrite)}>Yes</a>
                            <a href="" className="overwrite-choice" onClick={noDefault(z.CancelLeafletEdit)}>No</a>
                        </div>
                    : z.Mode() === ZoneOperationsMode.PromptManualEntry ?
                        <div>
                            How do you want to add an anchorage?
                            <br />
                            <a href="" onClick={noDefault(z.ContinueLeafletEdit)}>Draw on map</a>
                            <br />
                            <a href="" onClick={noDefault(() => z.Mode(ZoneOperationsMode.PerformManualEntry))}>Enter coordinates and radius</a>
                        </div>
                    : z.Mode() === ZoneOperationsMode.PerformManualEntry ?
                        <div className="manualEntry">
                            <div className="title">Anchorage Details</div>
                            <label className="header">Latitude</label>
                            <NumericInput value={z.AnchorageLatitude} />
                            <br />
                            <label className="header">Longitude</label>
                            <NumericInput value={z.AnchorageLongitude} />
                            <br />
                            <label className="header">Radius</label>
                            <ToggleableMeasurementView model={z.ToggleableRadius} />
                            <ToggleButton className="uom" onLabel="feet" offLabel="meters" checked={z.AnchorageIsFeet} />
                            <br />
                            {z.ErrorMessage() && <label className="error">{z.ErrorMessage()}</label>}
                            <br />
                            <a className="commands" href="" onClick={noDefault(z.SaveManualEntry)}>Save</a>
                            <a className="commands" href="" onClick={noDefault(z.CancelLeafletEdit)}>Cancel</a>
                        </div>
                    : null
                    )}
                </div>
            </div>
        </PCModal>
    );
});


// WEBPACK FOOTER //
// ./apps/portcall/berthprofile/view.tsx